import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'

/* ─── Constants ─── */
const COLS = ['A', 'B', 'C', 'D', 'E']
const ROWS = [1, 2, 3, 4]

const RANGE_EXAMPLES = [
  { label: 'A1:A4', desc: 'All of column A (rows 1-4)', color: 'bg-blue-200/60 border-blue-400' },
  { label: 'B2:D4', desc: 'A 3x3 block from B2 to D4', color: 'bg-emerald-200/60 border-emerald-400' },
  { label: 'A1:E1', desc: 'The entire first row', color: 'bg-amber-200/60 border-amber-400' },
  { label: 'C2:E3', desc: 'A 3x2 block from C2 to E3', color: 'bg-purple-200/60 border-purple-400' },
]

const QUIZ = [
  {
    question: 'What is the address of the cell in column C, row 5?',
    options: ['5C', 'C5', 'C:5', 'Row5ColC'],
    correctIndex: 1,
    explanation: 'A cell address is always the column letter followed by the row number. Column C, row 5 = C5.',
  },
  {
    question: 'What does the range A1:A10 refer to?',
    options: [
      'The first 10 columns',
      'The first 10 cells in column A',
      'Cell A1 and cell A10 only',
      'All cells in the spreadsheet',
    ],
    correctIndex: 1,
    explanation: 'A1:A10 means "start at A1, end at A10" -- that\'s the first 10 cells in column A.',
  },
  {
    question: 'If cell B2 contains =A2*2 and you copy it to B3, what does B3 contain?',
    options: ['=A2*2', '=A3*2', '=B3*2', '=A2*3'],
    correctIndex: 1,
    explanation: 'With relative references, when you copy a formula down one row, every row reference increases by 1. A2 becomes A3.',
  },
  {
    question: 'What does the $ sign do in $A$1?',
    options: [
      'Formats the cell as currency',
      'Deletes the cell',
      'Locks the reference so it doesn\'t change when copied',
      'Makes the formula run faster',
    ],
    correctIndex: 2,
    explanation: 'The $ sign creates an absolute reference. $A$1 means "always column A, always row 1" no matter where you copy the formula.',
  },
  {
    question: 'If cell C1 contains =$A1+B1 and you copy it to C2, what does C2 contain?',
    options: ['=$A$1+B1', '=$A1+B2', '=$A2+B2', '=$A2+B1'],
    correctIndex: 2,
    explanation: '$A locks the column (A stays A) but the row is relative, so row 1 shifts to row 2. B1 is fully relative, so it also shifts to B2. Result: =$A2+B2.',
  },
  {
    question: 'What does the range B2:D4 describe?',
    options: [
      'A single cell at position B2',
      'A rectangular block of cells from B2 to D4 (3 columns by 3 rows)',
      'Only cells B2 and D4',
      'The entire columns B through D',
    ],
    correctIndex: 1,
    explanation: 'A range like B2:D4 selects all cells in the rectangle from the top-left corner (B2) to the bottom-right corner (D4). That\'s columns B, C, D across rows 2, 3, 4 -- a 3x3 block.',
  },
  {
    question: 'If cell D1 contains =A1+$B$1 and you copy it to D2, what does D2 contain?',
    options: ['=A2+$B$1', '=A1+$B$2', '=A2+$B$2', '=A1+$B$1'],
    correctIndex: 0,
    explanation: 'A1 is a relative reference, so it shifts to A2 when copied down. $B$1 is fully absolute (both column and row locked), so it stays as $B$1. Result: =A2+$B$1.',
  },
  {
    question: 'You want a formula in C2 that always references column A but follows the row. Which reference should you use?',
    options: ['A2', '$A2', 'A$2', '$A$2'],
    correctIndex: 1,
    explanation: '$A2 is a mixed reference that locks the column (always A) but lets the row change. If you copy this formula to D3, it becomes $A3 -- still column A, but row shifted to 3.',
  },
  {
    question: 'What happens if you copy a formula from B2 to the right into C2?',
    options: [
      'Row references increase by 1',
      'Column references shift right by one letter',
      'Nothing changes',
      'The formula is deleted',
    ],
    correctIndex: 1,
    explanation: 'When you copy a formula horizontally (to the right), relative column references shift. A reference to A2 in B2 would become B2 in C2 -- the column letter moves right by one.',
  },
  {
    question: 'How many cells are in the range A1:C3?',
    options: ['3', '6', '9', '12'],
    correctIndex: 2,
    explanation: 'A1:C3 covers 3 columns (A, B, C) and 3 rows (1, 2, 3). That\'s 3 x 3 = 9 cells.',
  },
  {
    question: 'What does A$1 mean in a formula?',
    options: [
      'The column is locked, the row is relative',
      'Both column and row are locked',
      'The column is relative, the row is locked',
      'It formats the cell as currency',
    ],
    correctIndex: 2,
    explanation: 'A$1 is a mixed reference. The column (A) has no $ so it\'s relative and will change when copied sideways. The row ($1) is locked and won\'t change when copied up or down.',
  },
  {
    question: 'Cell A1 contains the number 10. Cell B1 contains =A1. What value does B1 display?',
    options: ['A1', '=A1', '10', 'An error'],
    correctIndex: 2,
    explanation: 'When a formula references another cell, it displays that cell\'s value. Since A1 contains 10, =A1 evaluates to 10.',
  },
]

/* ─── Helpers ─── */
function parseRange(rangeStr) {
  const match = rangeStr.match(/^([A-E])(\d):([A-E])(\d)$/)
  if (!match) return null
  const c1 = COLS.indexOf(match[1])
  const r1 = parseInt(match[2]) - 1
  const c2 = COLS.indexOf(match[3])
  const r2 = parseInt(match[4]) - 1
  return { c1, r1, c2, r2 }
}

function isInRange(range, col, row) {
  if (!range) return false
  return col >= range.c1 && col <= range.c2 && row >= range.r1 && row <= range.r2
}

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

/* ─── Section 1: What is a Cell? ─── */
function SectionCell() {
  const [activeCell, setActiveCell] = useState(null)

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">What is a Cell?</h2>
      <p className="mb-6 text-gray-600">
        A spreadsheet is a grid of <strong>cells</strong>. Every cell has a unique address made of
        its <span className="font-semibold text-blue-600">column letter</span> +{' '}
        <span className="font-semibold text-blue-600">row number</span>. Click any cell below to
        see its address.
      </p>

      {/* Formula bar */}
      <div className="mb-3 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Cell</span>
        <div className="h-5 w-px bg-gray-300" />
        <span className="font-mono text-sm font-bold text-blue-700">
          {activeCell ? `${COLS[activeCell.col]}${ROWS[activeCell.row]}` : '--'}
        </span>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <div className="inline-block">
          {/* Column headers */}
          <div className="flex">
            <div className="w-10 h-9" />
            {COLS.map((c) => (
              <div
                key={c}
                className="flex h-9 w-20 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-gray-200 first:border-l first:rounded-tl-md last:rounded-tr-md"
              >
                {c}
              </div>
            ))}
          </div>

          {/* Rows */}
          {ROWS.map((r, ri) => (
            <div key={r} className="flex">
              {/* Row header */}
              <div className="flex h-10 w-10 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-l border-gray-200">
                {r}
              </div>
              {COLS.map((c, ci) => {
                const isActive =
                  activeCell && activeCell.col === ci && activeCell.row === ri
                return (
                  <div
                    key={`${c}${r}`}
                    onClick={() => setActiveCell({ col: ci, row: ri })}
                    className={`flex h-10 w-20 cursor-pointer items-center justify-center border-b border-r border-gray-200 text-xs font-mono transition-all duration-150 ${
                      isActive
                        ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-400 ring-inset text-blue-800 font-bold z-10'
                        : 'bg-white hover:bg-blue-50'
                    }`}
                  >
                    {isActive && `${c}${r}`}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-500">
        <strong>B3</strong> means column B, row 3. This addressing system is how every formula knows
        where to find data.
      </p>
    </motion.section>
  )
}

/* ─── Section 2: What is a Range? ─── */
function SectionRange() {
  const [activeRange, setActiveRange] = useState(0)
  const parsed = parseRange(RANGE_EXAMPLES[activeRange].label)

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">What is a Range?</h2>
      <p className="mb-6 text-gray-600">
        A <strong>range</strong> is a rectangular group of cells, written as{' '}
        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm font-mono text-gray-800">
          start:end
        </code>
        . Click the examples below to see each range highlighted.
      </p>

      {/* Range buttons */}
      <div className="mb-4 flex flex-wrap gap-2">
        {RANGE_EXAMPLES.map((r, i) => (
          <button
            key={r.label}
            onClick={() => setActiveRange(i)}
            className={`rounded-lg border-2 px-4 py-2 text-sm font-mono font-semibold transition-all ${
              activeRange === i
                ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.p
          key={activeRange}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 5 }}
          className="mb-4 text-sm font-medium text-gray-700"
        >
          <span className="font-mono text-blue-600">{RANGE_EXAMPLES[activeRange].label}</span>
          {' = '}
          {RANGE_EXAMPLES[activeRange].desc}
        </motion.p>
      </AnimatePresence>

      {/* Grid with range highlight */}
      <div className="overflow-x-auto">
        <div className="inline-block">
          <div className="flex">
            <div className="w-10 h-9" />
            {COLS.map((c) => (
              <div
                key={c}
                className="flex h-9 w-20 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-gray-200 first:border-l first:rounded-tl-md last:rounded-tr-md"
              >
                {c}
              </div>
            ))}
          </div>
          {ROWS.map((r, ri) => (
            <div key={r} className="flex">
              <div className="flex h-10 w-10 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-l border-gray-200">
                {r}
              </div>
              {COLS.map((c, ci) => {
                const highlighted = isInRange(parsed, ci, ri)
                return (
                  <div
                    key={`${c}${r}`}
                    className={`flex h-10 w-20 items-center justify-center border-b border-r border-gray-200 text-xs font-mono transition-all duration-200 ${
                      highlighted
                        ? `${RANGE_EXAMPLES[activeRange].color} font-semibold`
                        : 'bg-white text-gray-400'
                    }`}
                  >
                    {c}{r}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  )
}

/* ─── Section 3: Relative vs Absolute References ─── */
function SectionReferences() {
  const [copied, setCopied] = useState(false)

  const rows = [
    { row: 2, a: 10, b: 20, relative: '=A2+B2', absolute: '=$A$2+B2', mixed: '=$A2+B$2' },
    { row: 3, a: 15, b: 25, relative: '=A3+B3', absolute: '=$A$2+B3', mixed: '=$A3+B$2' },
    { row: 4, a: 8,  b: 12, relative: '=A4+B4', absolute: '=$A$2+B4', mixed: '=$A4+B$2' },
  ]

  const columns = [
    { key: 'relative', label: 'Relative', color: 'bg-blue-50 text-blue-700' },
    { key: 'absolute', label: 'Absolute', color: 'bg-emerald-50 text-emerald-700' },
    { key: 'mixed', label: 'Mixed', color: 'bg-amber-50 text-amber-700' },
  ]

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">Relative vs Absolute References</h2>
      <p className="mb-6 text-gray-600">
        This is the <strong>most important concept</strong> for writing formulas that work when
        copied. The <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm font-mono">$</code>{' '}
        sign locks a reference in place. See how the same formula changes (or doesn&apos;t) when copied down.
      </p>

      {/* Combined grid */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-[700px]">
          {/* Column letters */}
          <div className="flex">
            <div className="w-10 h-9" />
            {['A', 'B'].map((c) => (
              <div
                key={c}
                className="flex h-9 w-20 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-gray-200 first:border-l"
              >
                {c}
              </div>
            ))}
            {columns.map((col) => (
              <div
                key={col.key}
                className="flex h-9 flex-1 min-w-[140px] items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-gray-200"
              >
                {col.label}
              </div>
            ))}
          </div>

          {/* Header row */}
          <div className="flex">
            <div className="flex h-10 w-10 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-l border-gray-200">
              1
            </div>
            {['Values A', 'Values B'].map((h) => (
              <div
                key={h}
                className="flex h-10 w-20 items-center justify-center border-b border-r border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600"
              >
                {h}
              </div>
            ))}
            {columns.map((col) => (
              <div
                key={col.key}
                className="flex h-10 flex-1 min-w-[140px] items-center justify-center border-b border-r border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600"
              >
                Formula ({col.label})
              </div>
            ))}
          </div>

          {/* Data rows */}
          {rows.map((row, i) => {
            const showFormula = i === 0 || copied
            return (
              <div key={row.row} className="flex">
                <div className="flex h-12 w-10 items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 border-b border-r border-l border-gray-200">
                  {row.row}
                </div>
                <div className="flex h-12 w-20 items-center justify-center border-b border-r border-gray-200 bg-white text-sm text-gray-700">
                  {row.a}
                </div>
                <div className="flex h-12 w-20 items-center justify-center border-b border-r border-gray-200 bg-white text-sm text-gray-700">
                  {row.b}
                </div>
                {columns.map((col) => (
                  <div
                    key={col.key}
                    className={`flex h-12 flex-1 min-w-[140px] items-center justify-center border-b border-r border-gray-200 text-sm font-mono transition-all duration-300 ${
                      showFormula
                        ? `${col.color} font-semibold`
                        : 'bg-gray-50 text-gray-400'
                    }`}
                  >
                    {showFormula ? row[col.key] : '?'}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>

      {/* Copy button */}
      <div className="mt-4 flex items-center gap-4">
        <button
          onClick={() => setCopied(true)}
          disabled={copied}
          className={`rounded-lg px-5 py-2.5 text-sm font-medium shadow-sm transition-colors ${
            copied
              ? 'bg-gray-300 text-gray-500 cursor-default'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          Copy formula to rows below
        </button>
        {copied && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-sm font-medium text-emerald-600"
          >
            Notice how $ keeps references locked!
          </motion.span>
        )}
      </div>

      {/* Explanation */}
      <AnimatePresence>
        {copied && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2"
          >
            <p className="text-sm text-blue-800"><strong>Relative</strong> — both references shift when copied down. A2 becomes A3, B2 becomes B3.</p>
            <p className="text-sm text-blue-800"><strong>Absolute</strong> — $A$2 is locked and always points to A2. B2 still shifts because it&apos;s relative.</p>
            <p className="text-sm text-blue-800"><strong>Mixed</strong> — $A locks the column (but row shifts). B$2 locks the row (but column could shift). Each $ only locks the part right after it.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  )
}

/* ─── Main Module Component ─── */
export default function Module1() {
  return (
    <ModuleLayout moduleId={1}>
      <div className="space-y-10">
        <SectionCell />
        <SectionRange />
        <SectionReferences />
        <Quiz moduleId={1} questions={QUIZ} />
      </div>
    </ModuleLayout>
  )
}
